<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-md-6">
          <h3>Consultar paquetes</h3>
        </div>
        <div class="col-md-6 text-end">
          <report-export-buttons
            title="Reporte de paquetes"
            subtitle="Consulta de paquetes"
            file-name="reporte-paquetes"
            :columns="reportColumns"
            :rows="reportRows"
            :disabled="loading"
          />
        </div>
      </div>
      
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Origen</label>
            <input v-model.trim="filters.origin" class="form-control" placeholder="Buscar origen" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Destino</label>
            <input v-model.trim="filters.destination" class="form-control" placeholder="Buscar destino" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Remitente</label>
            <input v-model.trim="filters.sender" class="form-control" placeholder="Remitente" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Destinatario</label>
            <input v-model.trim="filters.recipient" class="form-control" placeholder="Destinatario" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Estatus</label>
            <select v-model="filters.status" class="form-select">
              <option value="">Todos</option>
              <option value="PUBLICADO">PUBLICADO</option>
              <option value="ACEPTADO">ACEPTADO</option>
              <option value="EN_TRANSITO">EN_TRANSITO</option>
              <option value="ENTREGADO">ENTREGADO</option>
              <option value="RECHAZADO">RECHAZADO</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Pago</label>
            <select v-model="filters.paymentStatus" class="form-select">
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PAGADO">Pagado</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Categoria</label>
            <select v-model="filters.category" class="form-select">
              <option value="">Todas</option>
              <option value="DOCUMENTOS">Documentos</option>
              <option value="ELECTRONICOS">Electronicos</option>
              <option value="ROPA">Ropa</option>
              <option value="ALIMENTOS_NO_PERECEDEROS">Alimentos no perecederos</option>
              <option value="MASCOTAS">Mascotas</option>
              <option value="FARMACIA">Farmacia</option>
              <option value="COSMETICOS">Cosmeticos</option>
              <option value="OTROS">Otros</option>
            </select>
          </div>
          <div class="col-md-12 text-end">
            <button class="btn btn-primary me-2" @click="search">Buscar</button>
            <button class="btn btn-outline-danger" @click="clearFilters">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"><span class="visually-hidden">Cargando...</span></div>
    </div>
    <div v-else-if="errorMsg" class="alert alert-danger">{{ errorMsg }}</div>
    <div v-else class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Remitente</th>
                <th>Destinatario</th>
                <th>Origen</th>
                <th>Destino</th>
                <th>Peso/Dimensiones</th>
                <th>Categoria</th>
                <th class="text-end">Valor a pagar</th>
                <th>Estatus</th>
                <th>Pago</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="items.length === 0">
                <td colspan="10" class="text-center text-muted py-4">No hay paquetes.</td>
              </tr>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.senderName }}</td>
                <td>
                  <div class="fw-semibold">{{ item.recipientName }}</div>
                  <div class="small text-muted">{{ item.recipientPhone || '-' }}</div>
                </td>
                <td class="text-break">{{ item.originAddress }}</td>
                <td class="text-break">{{ item.destinationAddress }}</td>
                <td>
                  <div>{{ item.weightKg }} kg</div>
                  <div class="small text-muted">{{ item.lengthCm }}x{{ item.widthCm }}x{{ item.heightCm }} cm</div>
                </td>
                <td>{{ categoryLabel(item.category) }}</td>
                <td class="text-end fw-semibold">{{ formatMxn(offeredPrice(item)) }}</td>
                <td><span class="badge" :class="statusBadgeClass(item.status)">{{ item.status }}</span></td>
                <td>
                  <span class="badge" :class="paymentBadgeClass(item.paymentStatus)">
                    {{ paymentLabel(item.paymentStatus) }}
                  </span>
                </td>
                <td class="text-end text-nowrap">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary me-1"
                    @click="openPackageDetail(item)"
                  >
                    <vue-feather type="eye" size="14" class="align-middle me-1"></vue-feather>
                    Ver
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="remove(item.id)">Eliminar</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.totalPages > 1">
          <span class="small text-muted">
            Mostrando {{ items.length }} de {{ pagination.total }} paquetes
          </span>
          <ul class="pagination m-0 pagination-primary">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.page - 1)">&laquo;</a>
            </li>
            <li v-for="i in pageNumbers" :key="i" class="page-item" :class="{ active: i === pagination.page }">
              <a class="page-link" href="#" @click.prevent="goToPage(i)">{{ i }}</a>
            </li>
            <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.page + 1)">&raquo;</a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal al body: evita stacking/overflow del layout y Bootstrap como módulo no expone window.bootstrap -->
    <teleport to="body">
      <div
        id="packageDetailModal"
        ref="packageDetailModalRef"
        class="modal fade"
        tabindex="-1"
        aria-hidden="true"
      >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0 align-items-start">
            <h5 class="modal-title">Detalle del paquete</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
          </div>
          <div v-if="selectedPackage" class="modal-body pt-2">
            <div class="product-box row">
              <div class="product-img col-lg-5 mb-3 mb-lg-0">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center p-4 d-flex flex-column justify-content-center align-items-center">
                    <div
                      class="rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                      style="width: 140px; height: 140px; background: linear-gradient(145deg, #ecf4ff 0%, #fff8e6 100%);"
                    >
                      <vue-feather type="package" size="56" class="text-primary"></vue-feather>
                    </div>
                    <div class="product-price mb-1">
                      <span class="fs-3 fw-bold text-primary">{{ formatMxn(offeredPrice(selectedPackage)) }}</span>
                    </div>
                    <div class="text-muted small mb-2">Valor a pagar (MXN)</div>
                    <!-- <div v-if="estimatedPrice(selectedPackage) > 0" class="small text-muted mb-2">
                      Tarifa referencia estimada:
                      <span class="text-body">{{ formatMxn(estimatedPrice(selectedPackage)) }}</span>
                    </div> -->
                    <div v-if="declaredValue(selectedPackage) > 0" class="small text-muted">
                      Valor declarado:
                      <span class="text-body">{{ formatMxn(declaredValue(selectedPackage)) }}</span>
                    </div>
                  <div class="small text-muted mt-2">
                    Pago:
                    <span class="text-body fw-semibold">{{ paymentLabel(selectedPackage.paymentStatus) }}</span>
                  </div>
                    <span class="badge mt-3" :class="statusBadgeClass(selectedPackage.status)">{{ selectedPackage.status }}</span>
                  </div>
                </div>
              </div>
              <div class="product-details col-lg-7">
                <h6 class="f-w-600 mb-3">Información del envío</h6>
                <p class="text-muted small mb-3">
                  {{ selectedPackage.description || 'Sin descripción.' }}
                </p>
                <div class="row g-3">
                  <div class="col-12">
                    <label class="text-muted small mb-2 d-block">Simulación de flujo (actualizar estatus)</label>
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        :disabled="updatingPackageStatus || selectedPackage.status === 'ACEPTADO'"
                        @click="updateSelectedPackageStatus('ACEPTADO')"
                      >
                        {{ updatingPackageStatus && statusTarget === 'ACEPTADO' ? 'Actualizando...' : 'Marcar ACEPTADO' }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-warning"
                        :disabled="updatingPackageStatus || selectedPackage.status === 'EN_TRANSITO'"
                        @click="updateSelectedPackageStatus('EN_TRANSITO')"
                      >
                        {{ updatingPackageStatus && statusTarget === 'EN_TRANSITO' ? 'Actualizando...' : 'Marcar EN_TRANSITO' }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-info"
                        :disabled="updatingPackageStatus || selectedPackage.status === 'ENTREGADO'"
                        @click="updateSelectedPackageStatus('ENTREGADO')"
                      >
                        {{ updatingPackageStatus && statusTarget === 'ENTREGADO' ? 'Actualizando...' : 'Marcar ENTREGADO' }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="updatingPackageStatus || selectedPackage.status === 'RECHAZADO'"
                        @click="updateSelectedPackageStatus('RECHAZADO')"
                      >
                        {{ updatingPackageStatus && statusTarget === 'RECHAZADO' ? 'Actualizando...' : 'Marcar RECHAZADO' }}
                      </button>
                    </div>
                    <div class="small text-muted mt-2">
                      Al marcar <strong>ENTREGADO</strong> se dispara la liberación automática de fondos del escrow.
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Remitente</label>
                    <div class="fw-semibold">{{ selectedPackage.senderName }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Destinatario</label>
                    <div class="fw-semibold">{{ selectedPackage.recipientName }}</div>
                    <div class="small text-muted">{{ selectedPackage.recipientPhone || '—' }}</div>
                  </div>
                  <div class="col-12">
                    <label class="text-muted small mb-1 d-block">Origen</label>
                    <div class="text-break">{{ selectedPackage.originAddress }}</div>
                  </div>
                  <div class="col-12">
                    <label class="text-muted small mb-1 d-block">Destino</label>
                    <div class="text-break">{{ selectedPackage.destinationAddress }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Categoría</label>
                    <div>{{ categoryLabel(selectedPackage.category) }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Peso y medidas</label>
                    <div>{{ selectedPackage.weightKg }} kg</div>
                    <div class="small text-muted">
                      {{ selectedPackage.lengthCm }} × {{ selectedPackage.widthCm }} × {{ selectedPackage.heightCm }} cm
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Urgencia</label>
                    <div>{{ selectedPackage.urgency ? 'Sí' : 'No' }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">ID viaje</label>
                    <div class="small font-monospace text-break">{{ selectedPackage.tripId }}</div>
                  </div>
                  <div v-if="specialInstructionsText(selectedPackage)" class="col-12">
                    <label class="text-muted small mb-1 d-block">Instrucciones especiales</label>
                    <div class="small">{{ specialInstructionsText(selectedPackage) }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Alta</label>
                    <div class="small">{{ formatDateTime(selectedPackage.createdAt) }}</div>
                  </div>
                  <div class="col-sm-6">
                    <label class="text-muted small mb-1 d-block">Última actualización</label>
                    <div class="small">{{ formatDateTime(selectedPackage.updatedAt) }}</div>
                  </div>
                </div>

                <hr class="my-4" />

                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="f-w-600 mb-0">Historial de pagos</h6>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="loadingPayments || !selectedPackage?.id"
                    @click="syncSelectedPackagePayments"
                  >
                    {{ loadingPayments ? 'Sincronizando...' : 'Sincronizar con Stripe' }}
                  </button>
                </div>

                <div v-if="paymentsErrorMsg" class="alert alert-danger py-2 mb-3">{{ paymentsErrorMsg }}</div>
                <div v-if="loadingPayments" class="small text-muted mb-3">Cargando pagos...</div>

                <div v-else class="table-responsive mb-3">
                  <table class="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Estado</th>
                        <th>Monto</th>
                        <th>Fecha</th>
                        <th class="text-end">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="packagePayments.length === 0">
                        <td colspan="4" class="text-muted text-center py-3">Aún no hay pagos registrados.</td>
                      </tr>
                      <tr v-for="payment in packagePayments" :key="payment.id">
                        <td>
                          <span class="badge" :class="paymentStatusBadgeClass(payment.status)">
                            {{ paymentStatusLabel(payment.status) }}
                          </span>
                        </td>
                        <td>{{ formatMxn(payment.amount) }}</td>
                        <td>{{ formatDateTime(payment.createdAt) }}</td>
                        <td class="text-end">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-warning"
                            :disabled="refundLoading || !canRefund(payment.status)"
                            @click="openRefundDialog(payment)"
                          >
                            Reembolsar
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="selectedPaymentForRefund" class="card border">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong>Reembolso</strong>
                      <button class="btn btn-sm btn-outline-secondary" :disabled="refundLoading" @click="closeRefundDialog">
                        Cerrar
                      </button>
                    </div>
                    <div class="row g-2">
                      <div class="col-md-4">
                        <label class="form-label mb-1">Monto (MXN, opcional)</label>
                        <input
                          v-model.number="refundForm.amount"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control form-control-sm"
                          placeholder="Vacío = total disponible"
                        />
                      </div>
                      <div class="col-md-4">
                        <label class="form-label mb-1">Razón</label>
                        <select v-model="refundForm.reason" class="form-select form-select-sm">
                          <option value="">Seleccionar</option>
                          <option value="requested_by_customer">Solicitado por cliente</option>
                          <option value="duplicate">Duplicado</option>
                          <option value="fraudulent">Fraudulento</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label mb-1">Notas (opcional)</label>
                        <input
                          v-model.trim="refundForm.notes"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Notas internas"
                        />
                      </div>
                      <div class="col-12 text-end">
                        <button class="btn btn-sm btn-warning" :disabled="refundLoading" @click="submitRefund">
                          {{ refundLoading ? 'Procesando...' : 'Confirmar reembolso' }}
                        </button>
                      </div>
                    </div>

                    <div class="mt-3">
                      <div class="small fw-semibold mb-1">Reembolsos del pago</div>
                      <div v-if="loadingRefunds" class="small text-muted">Cargando reembolsos...</div>
                      <div v-else-if="selectedPaymentRefunds.length === 0" class="small text-muted">
                        No hay reembolsos registrados.
                      </div>
                      <div v-else class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Estado</th>
                              <th>Monto</th>
                              <th>Razón</th>
                              <th>Fecha</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="refund in selectedPaymentRefunds" :key="refund.id">
                              <td>
                                <span class="badge" :class="refundStatusBadgeClass(refund.status)">
                                  {{ refundStatusLabel(refund.status) }}
                                </span>
                              </td>
                              <td>{{ formatMxn(refund.amount) }}</td>
                              <td>{{ refund.reason || '-' }}</td>
                              <td>{{ formatDateTime(refund.createdAt) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="btn btn-success me-auto"
              :disabled="paying || !selectedPackage || selectedPackage.paymentStatus === 'PAGADO'"
              @click="paySelectedPackage"
            >
              {{ paying ? 'Redirigiendo...' : 'Pagar' }}
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
          </div>
        </div>
      </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { packagesApi } from '@/api/packages';
import { paymentsApi } from '@/api/payments';
import ReportExportButtons from '@/components/reports/ReportExportButtons.vue';

export default {
  name: 'ConsultarPaquetes',
  components: { ReportExportButtons },
  data() {
    return {
      selectedPackage: null,
      paying: false,
      loadingPayments: false,
      paymentsErrorMsg: '',
      packagePayments: [],
      selectedPaymentForRefund: null,
      selectedPaymentRefunds: [],
      loadingRefunds: false,
      refundLoading: false,
      updatingPackageStatus: false,
      statusTarget: '',
      refundForm: {
        amount: null,
        reason: '',
        notes: '',
      },
      items: [],
      loading: false,
      errorMsg: '',
      filters: {
        origin: '',
        destination: '',
        sender: '',
        recipient: '',
        status: '',
        category: '',
        paymentStatus: '',
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
      },
    };
  },
  computed: {
    pageNumbers() {
      const total = this.pagination.totalPages || 1;
      const current = this.pagination.page;
      const start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      const result = [];
      for (let i = start; i <= end; i += 1) result.push(i);
      return result;
    },
    reportColumns() {
      return [
        { key: 'senderName', label: 'Remitente' },
        { key: 'recipientName', label: 'Destinatario' },
        { key: 'originAddress', label: 'Origen' },
        { key: 'destinationAddress', label: 'Destino' },
        { key: 'category', label: 'Categoría' },
        { key: 'offeredPriceMxn', label: 'Valor a pagar' },
        { key: 'status', label: 'Estatus' },
        { key: 'paymentStatus', label: 'Pago' },
      ];
    },
    reportRows() {
      return this.items.map((item) => ({
        senderName: item.senderName || '-',
        recipientName: item.recipientName || '-',
        originAddress: item.originAddress || '-',
        destinationAddress: item.destinationAddress || '-',
        category: this.categoryLabel(item.category),
        offeredPriceMxn: this.formatMxn(this.offeredPrice(item)),
        status: item.status || '-',
        paymentStatus: this.paymentLabel(item.paymentStatus),
      }));
    },
  },
  mounted() {
    this.fetchData();
    this.syncFromCheckoutReturn();
    this.$nextTick(() => {
      const el = this.$refs.packageDetailModalRef;
      if (el) el.addEventListener('hidden.bs.modal', this.onPackageModalHidden);
    });
  },
  beforeUnmount() {
    const el = this.$refs.packageDetailModalRef;
    if (el) el.removeEventListener('hidden.bs.modal', this.onPackageModalHidden);
  },
  methods: {
    async syncFromCheckoutReturn() {
      const paymentResult = this.$route?.query?.payment;
      const packageId = this.$route?.query?.packageId;
      if (!packageId || (paymentResult !== 'success' && paymentResult !== 'cancel')) return;

      if (paymentResult === 'success') {
        try {
          await paymentsApi.syncByPackage(packageId);
          await this.fetchData();
          await Swal.fire({
            icon: 'success',
            title: 'Pago confirmado',
            text: 'Se sincronizó correctamente el estado del pago.',
            confirmButtonColor: '#0d6efd',
          });
        } catch {
          // Evitar bloquear UX; se puede sincronizar manualmente desde el detalle.
        }
      } else {
        await Swal.fire({
          icon: 'info',
          title: 'Pago cancelado',
          text: 'No se realizó el cobro.',
          confirmButtonColor: '#0d6efd',
        });
      }

      this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, payment: undefined, packageId: undefined } });
    },
    buildParams() {
      const params = {
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      if (this.filters.origin) params.origin = this.filters.origin;
      if (this.filters.destination) params.destination = this.filters.destination;
      if (this.filters.sender) params.sender = this.filters.sender;
      if (this.filters.recipient) params.recipient = this.filters.recipient;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.category) params.category = this.filters.category;
      if (this.filters.paymentStatus) params.paymentStatus = this.filters.paymentStatus;
      return params;
    },
    async fetchData() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await packagesApi.getAll(this.buildParams());
        this.items = data?.items || [];
        this.pagination.total = data?.total || 0;
        this.pagination.totalPages = data?.totalPages || 1;
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Error al cargar paquetes.';
      } finally {
        this.loading = false;
      }
    },
    async search() {
      this.pagination.page = 1;
      await this.fetchData();
    },
    async clearFilters() {
      this.filters = {
        origin: '',
        destination: '',
        sender: '',
        recipient: '',
        status: '',
        category: '',
        paymentStatus: '',
      };
      this.pagination.page = 1;
      await this.fetchData();
    },
    async goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.page) return;
      this.pagination.page = page;
      await this.fetchData();
    },
    async remove(id) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Eliminar paquete',
        text: 'Esta accion no se puede deshacer.',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
      });
      if (!confirm.isConfirmed) return;
      try {
        await packagesApi.delete(id);
        await this.fetchData();
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo eliminar el paquete.';
        await Swal.fire({ icon: 'error', title: 'Error', text: msg });
      }
    },
    categoryLabel(value) {
      return {
        DOCUMENTOS: 'Documentos',
        ELECTRONICOS: 'Electronicos',
        ROPA: 'Ropa',
        ALIMENTOS_NO_PERECEDEROS: 'Alimentos no perecederos',
        MASCOTAS: 'Mascotas',
        FARMACIA: 'Farmacia',
        COSMETICOS: 'Cosmeticos',
        OTROS: 'Otros',
      }[value] || value || '-';
    },
    statusBadgeClass(status) {
      return {
        PUBLICADO: 'bg-primary',
        ACEPTADO: 'bg-success',
        RECHAZADO: 'bg-danger',
        EN_TRANSITO: 'bg-warning text-dark',
        ENTREGADO: 'bg-info text-dark',
      }[status] || 'bg-secondary';
    },
    paymentBadgeClass(paymentStatus) {
      return paymentStatus === 'PAGADO' ? 'bg-success' : 'bg-warning text-dark';
    },
    paymentLabel(paymentStatus) {
      return paymentStatus === 'PAGADO' ? 'Pagado' : 'Pendiente';
    },
    offeredPrice(item) {
      if (!item) return 0;
      const v = item.offeredPriceMxn ?? item.offered_price_mxn;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    estimatedPrice(item) {
      if (!item) return 0;
      const v = item.estimatedPriceMxn ?? item.estimated_price_mxn;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    declaredValue(item) {
      if (!item) return 0;
      const v = item.declaredValueMxn ?? item.declared_value_mxn;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatMxn(amount) {
      const n = Number(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(n);
    },
    specialInstructionsText(item) {
      const s = item?.specialInstructions ?? item?.special_instructions;
      return s && String(s).trim() ? String(s).trim() : '';
    },
    formatDateTime(value) {
      if (!value) return '—';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX');
    },
    openPackageDetail(item) {
      this.selectedPackage = { ...item };
      this.paying = false;
      this.packagePayments = [];
      this.selectedPaymentForRefund = null;
      this.selectedPaymentRefunds = [];
      this.paymentsErrorMsg = '';
      this.fetchPackagePayments(item.id);
      this.$nextTick(() => {
        const el = this.$refs.packageDetailModalRef || document.getElementById('packageDetailModal');
        if (!el) return;
        Modal.getOrCreateInstance(el).show();
      });
    },
    async paySelectedPackage() {
      if (!this.selectedPackage?.id) return;
      if (this.selectedPackage.paymentStatus === 'PAGADO') return;
      this.paying = true;
      try {
        const { data } = await paymentsApi.createCheckoutSession({
          packageId: this.selectedPackage.id,
          currency: 'mxn',
        });
        const checkoutUrl = data?.checkoutUrl;
        if (!checkoutUrl) {
          throw new Error('No se recibió URL de checkout.');
        }
        window.location.href = checkoutUrl;
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'No se pudo iniciar el pago.';
        await Swal.fire({
          icon: 'error',
          title: 'Pago',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
        });
      } finally {
        this.paying = false;
      }
    },
    canRefund(paymentStatus) {
      return paymentStatus === 'succeeded' || paymentStatus === 'partially_refunded' || paymentStatus === 'refunded';
    },
    paymentStatusLabel(status) {
      return {
        pending: 'Pendiente',
        succeeded: 'Exitoso',
        failed: 'Fallido',
        canceled: 'Cancelado',
        refunded: 'Reembolsado',
        partially_refunded: 'Parcialmente reembolsado',
      }[status] || status;
    },
    paymentStatusBadgeClass(status) {
      return {
        pending: 'bg-warning text-dark',
        succeeded: 'bg-success',
        failed: 'bg-danger',
        canceled: 'bg-secondary',
        refunded: 'bg-danger',
        partially_refunded: 'bg-info text-dark',
      }[status] || 'bg-secondary';
    },
    refundStatusLabel(status) {
      return {
        pending: 'Pendiente',
        succeeded: 'Completado',
        failed: 'Fallido',
        canceled: 'Cancelado',
      }[status] || status;
    },
    refundStatusBadgeClass(status) {
      return {
        pending: 'bg-warning text-dark',
        succeeded: 'bg-success',
        failed: 'bg-danger',
        canceled: 'bg-secondary',
      }[status] || 'bg-secondary';
    },
    async fetchPackagePayments(packageId) {
      if (!packageId) return;
      this.loadingPayments = true;
      this.paymentsErrorMsg = '';
      try {
        const { data } = await paymentsApi.getByPackage(packageId);
        this.packagePayments = Array.isArray(data) ? data : [];
      } catch (e) {
        this.paymentsErrorMsg = e.response?.data?.message || 'No se pudo cargar el historial de pagos.';
      } finally {
        this.loadingPayments = false;
      }
    },
    async syncSelectedPackagePayments() {
      const packageId = this.selectedPackage?.id;
      if (!packageId) return;
      this.loadingPayments = true;
      this.paymentsErrorMsg = '';
      try {
        const { data } = await paymentsApi.syncByPackage(packageId);
        this.packagePayments = Array.isArray(data) ? data : [];
        await this.fetchData();
        await Swal.fire({
          icon: 'success',
          title: 'Pagos sincronizados',
          text: 'Se actualizó el estado de pagos con Stripe.',
          confirmButtonColor: '#0d6efd',
        });
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo sincronizar con Stripe.';
        this.paymentsErrorMsg = Array.isArray(msg) ? msg.join(' ') : msg;
      } finally {
        this.loadingPayments = false;
      }
    },
    async openRefundDialog(payment) {
      this.selectedPaymentForRefund = payment;
      this.refundForm = {
        amount: null,
        reason: '',
        notes: '',
      };
      await this.loadRefunds(payment.id);
    },
    closeRefundDialog() {
      this.selectedPaymentForRefund = null;
      this.selectedPaymentRefunds = [];
      this.refundForm = {
        amount: null,
        reason: '',
        notes: '',
      };
    },
    async loadRefunds(paymentId) {
      this.loadingRefunds = true;
      try {
        const { data } = await paymentsApi.getRefunds(paymentId);
        this.selectedPaymentRefunds = Array.isArray(data) ? data : [];
      } catch {
        this.selectedPaymentRefunds = [];
      } finally {
        this.loadingRefunds = false;
      }
    },
    async submitRefund() {
      if (!this.selectedPaymentForRefund?.id) return;
      this.refundLoading = true;
      try {
        const payload = {};
        if (this.refundForm.amount !== null && this.refundForm.amount !== undefined && this.refundForm.amount !== '') {
          payload.amount = Number(this.refundForm.amount);
        }
        if (this.refundForm.reason) payload.reason = this.refundForm.reason;
        if (this.refundForm.notes) payload.notes = this.refundForm.notes;

        await paymentsApi.createRefund(this.selectedPaymentForRefund.id, payload);
        await this.loadRefunds(this.selectedPaymentForRefund.id);
        await this.fetchPackagePayments(this.selectedPackage.id);
        await this.fetchData();
        await Swal.fire({
          icon: 'success',
          title: 'Reembolso creado',
          text: 'El reembolso fue enviado correctamente.',
          confirmButtonColor: '#0d6efd',
        });
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo crear el reembolso.';
        await Swal.fire({
          icon: 'error',
          title: 'Reembolso',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.refundLoading = false;
      }
    },
    async updateSelectedPackageStatus(status) {
      if (!this.selectedPackage?.id || !status) return;
      if (this.selectedPackage.status === status) return;
      this.updatingPackageStatus = true;
      this.statusTarget = status;
      try {
        const { data } = await packagesApi.updateStatus(this.selectedPackage.id, status);
        this.selectedPackage = { ...this.selectedPackage, ...(data || {}), status };
        await this.fetchData();
        await Swal.fire({
          icon: 'success',
          title: 'Estatus actualizado',
          text:
            status === 'ENTREGADO'
              ? 'Paquete marcado como ENTREGADO. Se intentó la liberación automática de fondos.'
              : `Paquete marcado como ${status}.`,
          confirmButtonColor: '#0d6efd',
        });
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo actualizar el estatus del paquete.';
        await Swal.fire({
          icon: 'error',
          title: 'Actualizar estatus',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.updatingPackageStatus = false;
        this.statusTarget = '';
      }
    },
    onPackageModalHidden() {
      this.selectedPackage = null;
      this.paying = false;
      this.loadingPayments = false;
      this.paymentsErrorMsg = '';
      this.packagePayments = [];
      this.selectedPaymentForRefund = null;
      this.selectedPaymentRefunds = [];
      this.loadingRefunds = false;
      this.refundLoading = false;
      this.updatingPackageStatus = false;
      this.statusTarget = '';
      this.refundForm = {
        amount: null,
        reason: '',
        notes: '',
      };
    },
  },
};
</script>

